import React, { useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Users, GraduationCap, HelpCircle } from 'lucide-react';

// NOTE: Radix Select disallows empty-string `value` on SelectItem, so we use
// a `__none` sentinel internally and translate to '' when emitting onChange.
const NONE = '__none';
const PRESETS = [
  { value: NONE,     label: 'No small-setting ratio', hint: 'General education classroom' },
  { value: '1:5',    label: '1:5',  hint: '1 teacher · 5 students' },
  { value: '4:1',    label: '4:1',  hint: '4 students · 1 teacher' },
  { value: '12:1',   label: '12:1', hint: '12 students · 1 teacher' },
  { value: '12:1:1', label: '12:1:1', hint: '12 students · 1 teacher · 1 paraprofessional (SPED)' },
  { value: '__custom', label: 'Custom…', hint: 'Type any ratio like 8:1 or 6:1:2' },
];

const isPreset = (v) => PRESETS.some((p) => p.value === v);

/**
 * RatioSelector
 * -------------
 * Small-setting classroom ratio picker (SPED / IEP style).
 * Value format: "students:teachers[:paras]" (e.g. "12:1:1") or "" (none).
 *
 * Props:
 *   value       — current ratio string (may be a preset or a custom value)
 *   onChange    — (newValue: string) => void
 *   label       — optional label above the control
 *   disabled    — pass through
 *   dataTestid  — root data-testid for the control
 */
const RatioSelector = ({
  value = '',
  onChange,
  label = 'Small-setting ratio',
  disabled = false,
  dataTestid = 'ratio-selector',
}) => {
  // If the passed value isn't a preset, treat it as custom
  const initialMode = !value ? NONE : (isPreset(value) ? value : '__custom');
  const [mode, setMode] = useState(initialMode);
  const [custom, setCustom] = useState(initialMode === '__custom' ? value : '');

  const hint = useMemo(() => {
    const preset = PRESETS.find((p) => p.value === mode);
    if (mode === '__custom') return 'Type any ratio like 8:1 or 6:1:2';
    return preset ? preset.hint : '';
  }, [mode]);

  const handleModeChange = (v) => {
    setMode(v);
    if (v === '__custom') {
      onChange && onChange(custom || '');
    } else if (v === NONE) {
      onChange && onChange('');
    } else {
      onChange && onChange(v);
    }
  };

  const handleCustomChange = (raw) => {
    // Allow only digits + colons while typing
    const cleaned = raw.replace(/[^0-9:]/g, '').slice(0, 12);
    setCustom(cleaned);
    if (mode === '__custom') onChange && onChange(cleaned);
  };

  return (
    <div className="space-y-1" data-testid={dataTestid}>
      {label && (
        <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
          <Users className="w-3 h-3 text-teal-600" />
          {label}
        </label>
      )}
      <Select value={mode} onValueChange={handleModeChange} disabled={disabled}>
        <SelectTrigger className="h-9 text-sm" data-testid={`${dataTestid}-trigger`}>
          <SelectValue placeholder="Choose a ratio" />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value} data-testid={`${dataTestid}-option-${p.value === NONE ? 'none' : p.value}`}>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-3.5 h-3.5 text-teal-600" />
                <span className="font-semibold">{p.label}</span>
                <span className="text-xs text-slate-500">— {p.hint}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {mode === '__custom' && (
        <Input
          value={custom}
          onChange={(e) => handleCustomChange(e.target.value)}
          placeholder="e.g. 8:1 or 6:1:2"
          className="h-9 text-sm mt-1"
          data-testid={`${dataTestid}-custom-input`}
        />
      )}
      {hint && (
        <p className="text-[11px] text-slate-500 flex items-center gap-1" data-testid={`${dataTestid}-hint`}>
          <HelpCircle className="w-3 h-3" /> {hint}
        </p>
      )}
    </div>
  );
};

export default RatioSelector;
